/**
 * Tribal Knowledge Engine — Institutional memory for maintenance teams.
 *
 * Pure functions that capture, index, search, and surface knowledge notes
 * attached to units and properties. Techs can leave notes like "shut-off
 * valve behind fridge" or "tenant prefers text before entry" that persist
 * across work orders and are surfaced to whoever visits next.
 *
 * Data model for a knowledge note:
 * {
 *   id: string,
 *   unitId: string,
 *   propertyId: string,
 *   category: 'access' | 'quirk' | 'history' | 'tenant' | 'safety' | 'tip',
 *   text: string,
 *   createdBy: string,          // tech ID or name
 *   createdAt: string,          // ISO date
 *   woId: string | null,        // linked work order (optional)
 *   upvotes: number,            // other techs confirming usefulness
 *   flagged: boolean,           // flagged as outdated/wrong
 *   tags: string[],             // freeform tags like ['plumbing', 'hvac']
 * }
 */

// ─── Categories ──────────────────────────────────────────────────────────────

export const KNOWLEDGE_CATEGORIES = {
  access:  { label: 'Access Info',     icon: 'key',         description: 'How to get in, gate codes, lockbox locations' },
  quirk:   { label: 'Unit Quirk',      icon: 'alert-circle', description: 'Weird plumbing, reversed valves, unusual layouts' },
  history: { label: 'Repair History',   icon: 'clock',       description: 'Past fixes, ongoing issues, known problems' },
  tenant:  { label: 'Tenant Pref',     icon: 'user',        description: 'Communication preferences, schedules, pets' },
  safety:  { label: 'Safety Note',     icon: 'shield',      description: 'Hazards, mold, asbestos, structural concerns' },
  tip:     { label: 'Pro Tip',         icon: 'lightbulb',   description: 'Shortcuts, tricks, best approaches for this unit' },
};

export const VALID_CATEGORIES = Object.keys(KNOWLEDGE_CATEGORIES);

// ─── Query Functions ─────────────────────────────────────────────────────────

/**
 * Get all knowledge notes for a specific unit, sorted by relevance.
 * Relevance = upvotes DESC, then newest first. Flagged notes sort to bottom.
 */
export function getNotesForUnit(notes, unitId) {
  if (!unitId || !Array.isArray(notes)) return [];

  return notes
    .filter(n => n.unitId === unitId)
    .sort((a, b) => {
      // Flagged notes go to bottom
      if (a.flagged !== b.flagged) return a.flagged ? 1 : -1;
      // Higher upvotes first
      if ((b.upvotes || 0) !== (a.upvotes || 0)) return (b.upvotes || 0) - (a.upvotes || 0);
      // Newer first
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
}

/**
 * Get all knowledge notes for a property (across all units).
 */
export function getNotesForProperty(notes, propertyId) {
  if (!propertyId || !Array.isArray(notes)) return [];

  return notes
    .filter(n => n.propertyId === propertyId)
    .sort((a, b) => {
      if (a.flagged !== b.flagged) return a.flagged ? 1 : -1;
      if ((b.upvotes || 0) !== (a.upvotes || 0)) return (b.upvotes || 0) - (a.upvotes || 0);
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
}

/**
 * Get notes filtered by category for a unit.
 */
export function getNotesByCategory(notes, unitId, category) {
  if (!VALID_CATEGORIES.includes(category)) return [];
  return getNotesForUnit(notes, unitId).filter(n => n.category === category);
}

/**
 * Search notes by text query (case-insensitive, matches text and tags).
 * Optionally scoped to a propertyId.
 */
export function searchNotes(notes, query, propertyId = null) {
  if (!query || typeof query !== 'string' || !Array.isArray(notes)) return [];

  const q = query.toLowerCase().trim();
  if (!q) return [];

  let pool = notes;
  if (propertyId) {
    pool = notes.filter(n => n.propertyId === propertyId);
  }

  return pool
    .filter(n => {
      const textMatch = (n.text || '').toLowerCase().includes(q);
      const tagMatch = (n.tags || []).some(t => t.toLowerCase().includes(q));
      const categoryMatch = (n.category || '').toLowerCase().includes(q);
      return textMatch || tagMatch || categoryMatch;
    })
    .sort((a, b) => {
      if (a.flagged !== b.flagged) return a.flagged ? 1 : -1;
      return (b.upvotes || 0) - (a.upvotes || 0);
    });
}

// ─── Surfacing / Relevance ───────────────────────────────────────────────────

/**
 * Get the most relevant notes to surface for a work order briefing.
 * Prioritizes: safety > access > tenant > quirk > tip > history.
 * Returns at most `limit` notes, preferring high-upvote non-flagged.
 */
const CATEGORY_PRIORITY = { safety: 0, access: 1, tenant: 2, quirk: 3, tip: 4, history: 5 };

export function getRelevantNotesForBriefing(notes, unitId, propertyId, limit = 5) {
  if (!Array.isArray(notes)) return [];

  // Combine unit-specific + property-wide notes (deduplicated)
  const seen = new Set();
  const combined = [];

  const unitNotes = notes.filter(n => n.unitId === unitId);
  const propertyNotes = notes.filter(n => n.propertyId === propertyId && n.unitId !== unitId);

  for (const n of [...unitNotes, ...propertyNotes]) {
    if (!seen.has(n.id)) {
      seen.add(n.id);
      combined.push(n);
    }
  }

  return combined
    .filter(n => !n.flagged)
    .sort((a, b) => {
      // Category priority first
      const catA = CATEGORY_PRIORITY[a.category] ?? 99;
      const catB = CATEGORY_PRIORITY[b.category] ?? 99;
      if (catA !== catB) return catA - catB;
      // Then upvotes
      return (b.upvotes || 0) - (a.upvotes || 0);
    })
    .slice(0, limit);
}

/**
 * Format knowledge notes as text for AI prompt injection.
 */
export function formatNotesForPrompt(notes) {
  if (!Array.isArray(notes) || notes.length === 0) return '';

  return notes.map(n => {
    const category = KNOWLEDGE_CATEGORIES[n.category]?.label || n.category;
    const upvoteStr = (n.upvotes || 0) > 0 ? ` (+${n.upvotes})` : '';
    return `[${category}]${upvoteStr}: ${n.text}`;
  }).join('\n');
}

// ─── Validation ──────────────────────────────────────────────────────────────

/**
 * Validate a knowledge note before saving.
 * Returns { valid: boolean, errors: string[] }.
 */
export function validateNote(note) {
  const errors = [];

  if (!note) {
    return { valid: false, errors: ['Note is required'] };
  }

  if (!note.unitId && !note.propertyId) {
    errors.push('Note must have a unitId or propertyId');
  }

  if (!note.text || typeof note.text !== 'string' || note.text.trim().length === 0) {
    errors.push('Note text is required');
  }

  if (note.text && note.text.length > 1000) {
    errors.push('Note text must be 1000 characters or less');
  }

  if (!note.category || !VALID_CATEGORIES.includes(note.category)) {
    errors.push(`Category must be one of: ${VALID_CATEGORIES.join(', ')}`);
  }

  if (!note.createdBy) {
    errors.push('createdBy is required');
  }

  return { valid: errors.length === 0, errors };
}

// ─── Analytics ───────────────────────────────────────────────────────────────

/**
 * Get knowledge coverage stats for a property.
 * Returns { totalNotes, unitsCovered, categoryCounts, topContributors }.
 */
export function getPropertyKnowledgeStats(notes, propertyId, units) {
  if (!Array.isArray(notes) || !propertyId) {
    return { totalNotes: 0, unitsCovered: 0, totalUnits: 0, categoryCounts: {}, topContributors: [] };
  }

  const propertyNotes = notes.filter(n => n.propertyId === propertyId && !n.flagged);
  const unitIds = new Set(propertyNotes.map(n => n.unitId).filter(Boolean));
  const totalUnits = Array.isArray(units) ? units.filter(u => u.propertyId === propertyId).length : 0;

  // Count by category
  const categoryCounts = {};
  for (const n of propertyNotes) {
    categoryCounts[n.category] = (categoryCounts[n.category] || 0) + 1;
  }

  // Top contributors
  const contributorMap = {};
  for (const n of propertyNotes) {
    contributorMap[n.createdBy] = (contributorMap[n.createdBy] || 0) + 1;
  }
  const topContributors = Object.entries(contributorMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({ name, count }));

  return {
    totalNotes: propertyNotes.length,
    unitsCovered: unitIds.size,
    totalUnits,
    categoryCounts,
    topContributors,
  };
}
